/**
 * Customer Module
 *
 * Authenticated customer account calls for code-lane storefronts. The edge
 * worker owns the HttpOnly session cookie and derives the shop from the host.
 * This module therefore sends neither a session token nor shop identity.
 */

import {
  customerLoyaltyRedeemSchema,
  customerLoyaltySchema,
  customerPortalDashboardSchema,
  customerPortalInvoiceDetailSchema,
  customerPortalPaginatedInvoicesSchema,
  customerWarrantyClaimSchema,
  customerWarrantyListSchema,
  type CustomerLoyaltyRedeemWire,
  type CustomerLoyaltyWire,
  type CustomerPortalDashboardWire,
  type CustomerPortalInvoiceDetailWire,
  type CustomerPortalPaginatedInvoicesWire,
  type CustomerWarrantyClaimWire,
  type CustomerWarrantyListWire,
} from '@shoppex/contracts';
import type { SDKResponse } from '../types';

const CUSTOMER_API_PREFIX = '/api/customer';

type CustomerMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * A producer-owned schema for the response payload.
 *
 * Only the endpoints whose shape is defined in `@shoppex/contracts` get one.
 * The rest stay `unknown` on purpose: inventing a type here would assert a wire
 * shape the producer never promised, which is the pattern the retired portal's
 * 544-line hand-written type file demonstrates the cost of.
 */
interface CustomerPayloadSchema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

interface CustomerRequestOptions<T> {
  method?: CustomerMethod;
  body?: unknown;
  /** Multipart payload. Mutually exclusive with `body`; the browser sets the boundary. */
  formData?: FormData;
  schema?: CustomerPayloadSchema<T>;
}

export interface CustomerOrdersQuery {
  page?: number;
  limit?: number;
  /**
   * Invoice status filter, verbatim from the producer's enum. Anything else is
   * answered with a 400 rather than quietly ignored, so pass what the buyer
   * actually chose.
   */
  status?: string;
  /** Partial invoice-id search. The producer trims it to 64 characters. */
  search?: string;
}

interface CustomerApiEnvelope {
  status: number;
  data: unknown;
  error?: unknown;
  message?: unknown;
  error_code?: unknown;
  error_params?: unknown;
}

export interface CustomerTicketPayload {
  title?: string;
  message: string;
  invoice_id?: string;
}

export interface CustomerProfilePatch {
  name: string;
}

export interface CustomerSubscriptionCancelOptions {
  cancel_at_period_end?: boolean;
  reason?: string | null;
}

export interface CustomerEmailPreferencesPatch {
  global_unsubscribed?: boolean;
  list_subscriptions?: Array<{ list_id: string; subscribed: boolean }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function narrowEnvelope(value: unknown): CustomerApiEnvelope | null {
  if (!isRecord(value) || typeof value.status !== 'number' || !('data' in value)) {
    return null;
  }

  return value as unknown as CustomerApiEnvelope;
}

function readMessage(envelope: CustomerApiEnvelope, response: Response): string {
  if (typeof envelope.error === 'string' && envelope.error.length > 0) {
    return envelope.error;
  }

  if (typeof envelope.message === 'string' && envelope.message.length > 0) {
    return envelope.message;
  }

  return response.statusText
    ? `HTTP ${response.status}: ${response.statusText}`
    : `HTTP ${response.status}`;
}

function readErrorFields(envelope: CustomerApiEnvelope): Pick<SDKResponse<unknown>, 'code' | 'errorParams'> {
  const code = typeof envelope.error_code === 'string' && envelope.error_code.length > 0
    ? envelope.error_code
    : null;

  if (!code) {
    return {};
  }

  return {
    code,
    ...(isRecord(envelope.error_params) ? { errorParams: envelope.error_params } : {}),
  };
}

async function requestCustomer<T = unknown>(
  path: string,
  options: CustomerRequestOptions<T> = {},
): Promise<SDKResponse<T>> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  // FormData carries its own multipart boundary in the Content-Type the browser
  // generates; setting the header here would produce a body the server cannot parse.
  const payload = options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined);

  try {
    const response = await fetch(`${CUSTOMER_API_PREFIX}${path}`, {
      method,
      headers,
      body: payload,
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (response.status === 204) {
      return response.ok
        ? { success: true, status: response.status }
        : {
          success: false,
          status: response.status,
          message: readMessage({ status: response.status, data: null }, response),
        };
    }

    let rawEnvelope: unknown;
    try {
      rawEnvelope = await response.json();
    } catch {
      return { success: false, status: response.status, message: 'Invalid customer API response' };
    }

    const envelope = narrowEnvelope(rawEnvelope);
    if (!envelope) {
      return { success: false, status: response.status, message: 'Invalid customer API response' };
    }

    if (!response.ok || envelope.status < 200 || envelope.status >= 300) {
      return {
        success: false,
        // The transport status, not the envelope's: a caller deciding whether
        // the buyer is signed out must not be steered by a body the edge may
        // never have produced.
        status: response.status,
        message: readMessage(envelope, response),
        ...readErrorFields(envelope),
      };
    }

    const message = typeof envelope.message === 'string' && envelope.message.length > 0
      ? { message: envelope.message }
      : {};

    if (!options.schema) {
      return { success: true, data: envelope.data as T, ...message };
    }

    const parsed = options.schema.safeParse(envelope.data);
    if (!parsed.success) {
      // Fail loudly instead of handing back a payload that does not match what
      // the producer promised. A silently reshaped or partially-read response
      // is how a contract drift reaches the buyer's screen as wrong data.
      return { success: false, message: 'Customer API response did not match the expected contract' };
    }

    return { success: true, data: parsed.data, ...message };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

/** One basket line for a QUOTE. `variant_id` is omitted, never null, when a product has no variants. */
export interface ResellerOrderItem {
  product_id: string;
  variant_id?: string;
  quantity: number;
}

export interface ResellerCatalogQuery {
  search?: string;
  page?: number;
  per_page?: number;
}

export interface ResellerOrdersQuery {
  page?: number;
  per_page?: number;
}

export function requestOtp(email: string): Promise<SDKResponse<unknown>> {
  return requestCustomer('/auth/otp/request', {
    method: 'POST',
    body: { email },
  });
}

/**
 * The wire field is `otp`, not `code` — `CustomerOtpVerifyBodySchema` in
 * `apps/backend-elysia/src/routes/v1/customer/schemas.ts` requires it under that
 * name, and a `code` body is rejected with 400.
 */
export function verifyOtp(email: string, otp: string): Promise<SDKResponse<unknown>> {
  return requestCustomer('/auth/otp/verify', {
    method: 'POST',
    body: { email, otp },
  });
}

export function logout(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/auth/logout', { method: 'POST' });
}

export function me(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/me');
}

export function dashboard(): Promise<SDKResponse<CustomerPortalDashboardWire>> {
  return requestCustomer('/dashboard', { schema: customerPortalDashboardSchema });
}

/**
 * Paginated order history.
 *
 * Page-based, matching the producer: `invoices.routes.ts` reads `page` and
 * `limit` and answers with a `has_more` envelope. A cursor parameter would be
 * ignored upstream and silently return page one forever.
 *
 * `status` and `search` are filtered SERVER-side, which is why they belong
 * here rather than in the caller: `/invoices` is paged, and a filter applied
 * to the page in hand would filter only the rows that page happens to hold.
 * The producer spells the search `q`.
 */
export function orders(options: CustomerOrdersQuery = {}): Promise<SDKResponse<CustomerPortalPaginatedInvoicesWire>> {
  const query = new URLSearchParams();
  if (options.page !== undefined) query.set('page', String(options.page));
  if (options.limit !== undefined) query.set('limit', String(options.limit));
  if (options.status) query.set('status', options.status);
  if (options.search) query.set('q', options.search);

  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return requestCustomer(`/invoices${suffix}`, { schema: customerPortalPaginatedInvoicesSchema });
}

export function order(id: string): Promise<SDKResponse<CustomerPortalInvoiceDetailWire>> {
  return requestCustomer(`/invoice/${encodeURIComponent(id)}`, {
    schema: customerPortalInvoiceDetailSchema,
  });
}

export function loyalty(): Promise<SDKResponse<CustomerLoyaltyWire>> {
  return requestCustomer('/loyalty', { schema: customerLoyaltySchema });
}

export function redeemLoyaltyPoints(input: {
  points: number;
  idempotencyKey: string;
}): Promise<SDKResponse<CustomerLoyaltyRedeemWire>> {
  return requestCustomer('/loyalty/redeem', {
    method: 'POST',
    body: {
      points: input.points,
      idempotency_key: input.idempotencyKey,
    },
    schema: customerLoyaltyRedeemSchema,
  });
}

/**
 * The warranties this buyer holds — all of them, or the ones on one order.
 *
 * `invoiceUniqid` becomes the producer's own `invoice=` filter, which matches
 * `invoices.uniqid`: the id an order carries everywhere a storefront shows one.
 * So an order detail asks for its own cover rather than reading every warranty
 * the buyer owns and discarding most of them.
 */
export function warranties(invoiceUniqid?: string): Promise<SDKResponse<CustomerWarrantyListWire>> {
  const suffix = invoiceUniqid ? `?invoice=${encodeURIComponent(invoiceUniqid)}` : '';
  return requestCustomer(`/warranties${suffix}`, { schema: customerWarrantyListSchema });
}

export function claimWarranty(
  uniqid: string,
  message?: string,
): Promise<SDKResponse<CustomerWarrantyClaimWire>> {
  return requestCustomer(`/warranties/${encodeURIComponent(uniqid)}/claim`, {
    method: 'POST',
    body: message ? { message } : {},
    schema: customerWarrantyClaimSchema,
  });
}

// ---------------------------------------------------------------------------
// Licenses, subscriptions and tickets have NO collection route.
//
// Those three lists arrive inside `/dashboard` — `licenses.routes.ts`,
// `subscriptions.routes.ts` and `tickets.routes.ts` expose per-item routes only.
// A `GET /licenses` here would 404 on every call, which is what the earlier
// spelling of this module did.
// ---------------------------------------------------------------------------

export function resetLicenseHwid(uniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/licenses/${encodeURIComponent(uniqid)}/reset-hwid`, {
    method: 'POST',
    body: {},
  });
}

export function subscriptionBillingHistory(uniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/subscriptions/${encodeURIComponent(uniqid)}/billing-history`);
}

export function cancelSubscription(
  uniqid: string,
  options: CustomerSubscriptionCancelOptions = {},
): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/subscriptions/${encodeURIComponent(uniqid)}/cancel`, {
    method: 'POST',
    body: {
      ...(options.cancel_at_period_end !== undefined
        ? { cancel_at_period_end: options.cancel_at_period_end }
        : {}),
      ...(options.reason !== undefined ? { reason: options.reason } : {}),
    },
  });
}

export function pauseSubscription(uniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/subscriptions/${encodeURIComponent(uniqid)}/pause`, {
    method: 'POST',
    body: {},
  });
}

export function resumeSubscription(uniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/subscriptions/${encodeURIComponent(uniqid)}/resume`, {
    method: 'POST',
    body: {},
  });
}

export function favorites(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/favorites');
}

/** Idempotent upstream, and a PUT — `customer-favorites.ts` has no POST route. */
export function addFavorite(productUniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/favorites/${encodeURIComponent(productUniqid)}`, { method: 'PUT' });
}

export function removeFavorite(productUniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/favorites/${encodeURIComponent(productUniqid)}`, { method: 'DELETE' });
}

export function affiliate(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/affiliate');
}

export function affiliateStats(days?: number): Promise<SDKResponse<unknown>> {
  const suffix = days === undefined ? '' : `?days=${encodeURIComponent(String(days))}`;
  return requestCustomer(`/affiliate/stats${suffix}`);
}

export function createTicket(payload: CustomerTicketPayload): Promise<SDKResponse<unknown>> {
  return requestCustomer('/tickets', {
    method: 'POST',
    body: {
      ...(payload.title !== undefined ? { title: payload.title } : {}),
      message: payload.message,
      ...(payload.invoice_id !== undefined ? { invoice_id: payload.invoice_id } : {}),
    },
  });
}

export function ticket(uniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/tickets/${encodeURIComponent(uniqid)}`);
}

export function replyToTicket(uniqid: string, message: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/tickets/${encodeURIComponent(uniqid)}/reply`, {
    method: 'POST',
    body: { message },
  });
}

/**
 * The producer is `POST /v1/customer/profile`, not PATCH.
 *
 * There is no `GET /profile` to pair with it — the buyer's own record comes from
 * `/me` and `/dashboard`.
 */
export function updateProfile(patch: CustomerProfilePatch): Promise<SDKResponse<unknown>> {
  return requestCustomer('/profile', {
    method: 'POST',
    body: { name: patch.name },
  });
}

export function updateAvatar(file: File): Promise<SDKResponse<unknown>> {
  const form = new FormData();
  form.append('file', file);
  return requestCustomer('/profile/avatar', { method: 'POST', formData: form });
}

export function removeAvatar(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/profile/avatar', { method: 'DELETE' });
}

export function emailPreferences(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/email-marketing/preferences');
}

export function updateEmailPreferences(
  patch: CustomerEmailPreferencesPatch,
): Promise<SDKResponse<unknown>> {
  return requestCustomer('/email-marketing/preferences', {
    method: 'PATCH',
    body: {
      ...(patch.global_unsubscribed !== undefined
        ? { global_unsubscribed: patch.global_unsubscribed }
        : {}),
      ...(patch.list_subscriptions !== undefined
        ? { list_subscriptions: patch.list_subscriptions }
        : {}),
    },
  });
}

export function sessions(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/sessions');
}

export function revokeSession(id: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Ends every session except this one.
 *
 * `others=true` is required, not decorative: `RevokeOtherSessionsQuerySchema`
 * declares it as a literal, so the route answers 400 without it. The selector
 * is explicit on purpose — "revoke sessions" with no qualifier is one typo away
 * from signing the buyer out of the device they are holding.
 */
export function revokeAllSessions(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/sessions?others=true', { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Wholesale (reseller program)
//
// A second program on the same account: its own way in, its own prepaid
// balance, its own prices. Every route re-checks "program enabled AND this
// reseller is ACTIVE" server-side, so nothing a storefront caches can widen
// what a buyer may do here.
//
// Two writes are deliberately absent, and adding them back would be adding
// methods that answer 404: the storefront worker refuses them. On a code
// storefront the JavaScript on this origin is the MERCHANT's and it holds the
// buyer's cookie, so the edge's same-origin check proves that script sent the
// request, never that the buyer wanted it.
//   - placing a wholesale order debits the buyer's prepaid balance; a purchase
//     needs a boundary the merchant does not control
//   - minting an API key hands back a plaintext credential that keeps
//     authorizing orders after the session ends
// Pricing a basket stays, because it writes nothing.
// ---------------------------------------------------------------------------

/** The program's state and this buyer's place in it. Null reseller means not enrolled. */
export function reseller(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/reseller');
}

/** `APPLICATION` mode. The note is optional unless the shop requires one. */
export function applyForReseller(note?: string): Promise<SDKResponse<unknown>> {
  return requestCustomer('/reseller/apply', {
    method: 'POST',
    body: note === undefined ? {} : { note },
  });
}

/** `OPEN` mode: no application, the buyer is a reseller when they say so. */
export function enrollAsReseller(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/reseller/enroll', { method: 'POST', body: {} });
}

/** `MANUAL` mode: the merchant invited this buyer and gave them a token. */
export function acceptResellerInvite(token: string): Promise<SDKResponse<unknown>> {
  return requestCustomer('/reseller/accept-invite', { method: 'POST', body: { token } });
}

/** The buyer's own tier prices. Page-based, like the order history. */
export function resellerCatalog(options: ResellerCatalogQuery = {}): Promise<SDKResponse<unknown>> {
  const query = new URLSearchParams();
  if (options.search !== undefined) query.set('search', options.search);
  if (options.page !== undefined) query.set('page', String(options.page));
  if (options.per_page !== undefined) query.set('per_page', String(options.per_page));

  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return requestCustomer(`/reseller/catalog${suffix}`);
}

/**
 * Prices a basket and writes nothing.
 *
 * The volume discount cannot be derived from the catalog — it only reports that
 * one exists — so a storefront that adds up unit prices itself shows a total
 * the shop will not charge. This is the number to display.
 */
export function quoteResellerOrder(items: ResellerOrderItem[]): Promise<SDKResponse<unknown>> {
  return requestCustomer('/reseller/orders/quote', { method: 'POST', body: { items } });
}

export function resellerOrders(options: ResellerOrdersQuery = {}): Promise<SDKResponse<unknown>> {
  const query = new URLSearchParams();
  if (options.page !== undefined) query.set('page', String(options.page));
  if (options.per_page !== undefined) query.set('per_page', String(options.per_page));

  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return requestCustomer(`/reseller/orders${suffix}`);
}

/** One order with its lines, delivery state and delivered serials. */
export function resellerOrder(uniqid: string): Promise<SDKResponse<unknown>> {
  return requestCustomer(`/reseller/orders/${encodeURIComponent(uniqid)}`);
}

/** The prepaid balance the wholesale orders are paid from. */
export function resellerWallet(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/reseller/wallet');
}

export function resellerApiKeys(): Promise<SDKResponse<unknown>> {
  return requestCustomer('/reseller/api-keys');
}

/*
 * There is no `revokeResellerApiKey`. Revoking is irreversible and stops
 * whatever the key was driving, and this SDK is the transport for code
 * storefronts — merchant-authored JavaScript running with the buyer's session,
 * where a confirmation proves nothing because the merchant renders it. The
 * edge omits the DELETE from `CUSTOMER_API_ALLOWLIST`
 * (`workers/storefront/src/customer-api.ts`), so exporting a method for it
 * would advertise a call that always answers 404.
 */
